import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Footer } from './Footer';
import { ChatWidget } from '../ChatWidget/ChatWidget';
import { useAuth } from '../../contexts/AuthContext';
import { scopedPath } from '../../utils/shopSlug';
import { usePortalNavigation, usePortalNavigationState } from '../../hooks/usePortalNavigation';
import { portalPageEnabled, portalRoutePath } from '../../utils/portalNavigation';

/**
 * Wave 9 (Frontend audit B3 + C5): persistent [Demo Mode] banner.
 *
 * Shown above the header whenever the current vendor session is a
 * demo vendor (Wave 8 backend `Vendor.is_demo === true`). Stays
 * visible across every page so the merchant never forgets they're
 * in the sandbox — important because demo products + orders never
 * appear on the storefront and aren't real.
 *
 * Color: amber/yellow tone (warning), not red (which signals error).
 * Scopes the banner to authenticated layout only — login/register
 * pages don't render it (you can't be logged in as a demo vendor
 * without auth).
 */
function DemoModeBanner() {
  const { user, vendor } = useAuth();
  const isDemo = !!(user?.is_demo || vendor?.is_demo);
  if (!isDemo) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-300 px-4 py-2.5 text-center dark:bg-amber-900/20 dark:border-amber-700">
      <p className="text-sm text-amber-900 dark:text-amber-200">
        <span className="font-semibold">🛈 Demo Mode</span> — products
        and orders here are sandbox-only and won't appear on your
        storefront. Sign out and log back in as a real vendor to make
        live changes.
      </p>
    </div>
  );
}

/**
 * Persistent "Admin mode" banner — shown whenever THIS session is a merchant
 * admin managing the store on the vendor's behalf ("Manage as vendor"). Keeps
 * the admin from ever forgetting whose account they're acting in, states the
 * reason they entered, and offers a one-click exit. Distinct orange tone from
 * the amber demo banner. Backed by `vendor.impersonation` (set from
 * GET /vendor/profile — authoritative across reloads).
 */
function ImpersonationBanner() {
  const { vendor, logout } = useAuth();
  const navigation = usePortalNavigation();
  const imp = vendor?.impersonation;
  if (!imp) return null;

  return (
    <div className="border-b border-orange-300 bg-orange-50 px-4 py-2.5 dark:border-orange-700 dark:bg-orange-900/25">
      <div className="mx-auto flex max-w-[1600px] flex-col items-center justify-between gap-1.5 sm:flex-row">
        <p className="text-sm text-orange-900 dark:text-orange-200">
          <span className="font-semibold">🛡️ Admin mode</span> — you're managing this
          store on the vendor's behalf.{' '}
          {imp.reason ? <span>Reason: “{imp.reason}”. </span> : null}
          Every change is recorded and the vendor is notified.
        </p>
        <div className="flex shrink-0 items-center gap-3">
          {portalPageEnabled('/activity', navigation) && <Link
            to={scopedPath('/activity')}
            className="text-sm font-medium text-orange-800 underline hover:text-orange-900 dark:text-orange-200"
          >
            View changes
          </Link>}
          <button
            type="button"
            onClick={() => { void logout().catch(() => undefined); }}
            className="rounded-md border border-orange-400 bg-white px-3 py-1 text-sm font-medium text-orange-900 hover:bg-orange-100 dark:bg-transparent dark:text-orange-100"
          >
            Exit admin mode
          </button>
        </div>
      </div>
    </div>
  );
}

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigationState = usePortalNavigationState();
  const navigation = navigationState.isError ? undefined : navigationState.data;
  const { pathname } = useLocation();
  const routePath = portalRoutePath(pathname);
  const pageEnabled = portalPageEnabled(routePath, navigation);

  return (
    <div className="flex h-screen min-h-[100dvh] overflow-hidden bg-surface dark:bg-gray-950">
      <a
        href="#main-content"
        className="fixed left-4 top-3 z-[60] -translate-y-20 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-lg transition-transform focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-primary"
      >
        Skip to content
      </a>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <ImpersonationBanner />
        <DemoModeBanner />
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1600px] p-4 pb-10 sm:p-5 lg:p-8">
            {!navigation ? <div role="status" className="card p-6">
              <h1 className="text-xl font-semibold">{navigationState.isError ? 'Portal pages unavailable' : 'Loading portal pages'}</h1>
              {navigationState.isError && <>
                <p className="mt-2">Your portal pages could not be loaded. Try again or contact your marketplace admin.</p>
                <button type="button" className="btn btn-secondary mt-3" onClick={() => { void navigationState.refetch(); }}>Try again</button>
              </>}
            </div> : pageEnabled ? <Outlet /> : <div role="status" className="card p-6">
              <h1 className="text-xl font-semibold">Page disabled by your marketplace</h1>
              <p className="mt-2">Choose an enabled page in the sidebar or contact your marketplace admin.</p>
            </div>}
          </div>
        </main>

        <Footer />
      </div>

      {/* Wave 10 (Frontend audit C4): ChatWidget gated to approved
          vendors. Pre-fix it was mounted unconditionally — pending /
          rejected / suspended vendors saw the support chat which is
          confusing because they can't yet take the actions the chat
          might suggest. */}
      <ApprovedVendorOnly>
        {portalPageEnabled('/support', navigation) && <ChatWidget />}
      </ApprovedVendorOnly>
    </div>
  );
}

function ApprovedVendorOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.status !== 'approved' && user?.status !== 'active') return null;
  return <>{children}</>;
}
